
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { translations } from '@/translations';
import { EnhancedTranslationFixer } from '@/utils/enhancedTranslationFixer';
import { DevelopmentTranslationMonitor, createMonitoredTranslationFunction } from '@/utils/translation/developmentMonitor';

type Language = 'en' | 'pt' | 'es';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, any>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    // Provide a fallback function when context is not available
    console.warn('useLanguage must be used within a LanguageProvider. Using fallback English translations.');
    return {
      language: 'en' as Language,
      setLanguage: () => {},
      t: (key: string) => {
        const keys = key.split('.');
        let value: any = translations.en;
        for (const k of keys) {
          if (value && typeof value === 'object' && k in value) {
            value = value[k];
          } else {
            return key;
          }
        }
        return typeof value === 'string' ? value : key;
      }
    };
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    try {
      const saved = localStorage.getItem('preferredLanguage') as Language | null;
      if (saved && ['en', 'pt', 'es'].includes(saved)) {
        return saved;
      }
    } catch {}
    return 'en';
  });

  // Initialize monitoring systems
  const devMonitor = DevelopmentTranslationMonitor.getInstance();
  const fixer = EnhancedTranslationFixer.getInstance();

  // Load saved language preference on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferredLanguage') as Language | null;
    
    if (savedLanguage && ['en', 'pt', 'es'].includes(savedLanguage)) {
      setLanguage(savedLanguage);
      console.log('Loaded saved language:', savedLanguage);
    } else {
      // Default to English only if no valid preference exists
      localStorage.setItem('preferredLanguage', 'en');
      setLanguage('en');
      console.log('No saved language, defaulting to English');
    }

    // Enable development monitoring
    if (process.env.NODE_ENV === 'development') {
      devMonitor.enable();
    }
  }, []);

  // Save language preference when it changes
  const handleSetLanguage = useCallback((lang: Language) => {
    console.log('Setting language to:', lang);
    setLanguage(lang);
    localStorage.setItem('preferredLanguage', lang);
  }, []);

  // Enhanced translation function with comprehensive monitoring and session fixes support
  const t = (key: string, params?: Record<string, any>): string => {
    // First check for session fixes (only for non-English languages)
    if (language !== 'en') {
      const sessionFix = fixer.getSessionFix(language, key);
      if (sessionFix) {
        // Apply parameter interpolation to session fix
        if (params) {
          return Object.entries(params).reduce((str, [param, val]) => {
            return str.replace(new RegExp(`{{${param}}}`, 'g'), String(val));
          }, sessionFix);
        }
        return sessionFix;
      }
    }

    // Use monitored translation function for development
    if (process.env.NODE_ENV === 'development') {
      const monitoredTranslate = createMonitoredTranslationFunction(language, translations, devMonitor);
      return monitoredTranslate(key, params);
    }

    // Regular translation lookup for production
    const keys = key.split('.');
    let value: any = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English if key not found
        value = translations.en;
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            return key; // Return the key if translation not found
          }
        }
        break;
      }
    }

    if (typeof value === 'string') {
      // Simple parameter interpolation
      if (params) {
        return Object.entries(params).reduce((str, [param, val]) => {
          return str.replace(new RegExp(`{{${param}}}`, 'g'), String(val));
        }, value);
      }
      return value;
    }

    return key;
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage: handleSetLanguage, 
      t
    }}>
      {children}
    </LanguageContext.Provider>
  );
};
